import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { UnitStatusSearchForm } from '@mark43/cad-api';
import { unitStatusSearchFormDefaultValues } from '../configuration';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { UnitStatusSearchFormType } from '../types';
import { FrPage } from '../../fr-page-layout/fr-page';
import { UnitStatusSearchSavedSearches } from '../unit-status-seach-saved-searches';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { UnitStatusSearchResults } from './unit-status-search-results';
import { FormWrapper } from '../../fr-page-layout/search-pages/form-wrapper';
import { useStationAndStatusOptions } from '../../unit-queues/components/get-unit-queue-filter-options';
import { PageHeader } from '../../fr-page-layout/search-pages/page-header';
import { UnitStatusSearchFormBody } from '../unit-status-search-form-body';
import { UnitStatusSearchFormFooter } from '../unit-status-search-form-footer';
import { useSearchState } from '../../fr-page-layout/search-pages/use-search-state';
import { clearWindowHistoryState } from '../../common/helpers/utils';

export function UnitStatusSearch() {
    const {
        mutation,
        resultsOrSavedShown,
        showSavedSearches,
        runSavedSearch,
        closeSearches,
        openSavedSearch,
        searchData,
        savedSearchId,
        uniqueSearch,
        setSearchData,
        setSavedSearchId,
        showSearchResults,
    } = useSearchState<UnitStatusSearchFormType, UnitStatusSearchForm>();

    const location = useLocation();
    const { defaultFuzzyMatchingEnabled } = useEnabledFeatures();

    const defaultValues = useMemo(
        () => ({
            ...unitStatusSearchFormDefaultValues,
            fuzzyMatchingEnabled: defaultFuzzyMatchingEnabled,
        }),
        [defaultFuzzyMatchingEnabled]
    );

    const form = useForm<UnitStatusSearchFormType>({
        mode: 'onBlur',
        defaultValues,
    });

    const query = useStationAndStatusOptions();

    const fuzzyMatchingEnabled = useWatch({
        control: form.control,
        name: 'fuzzyMatchingEnabled',
    });

    useEffect(
        function setSavedUnitStatusSearchData() {
            const savedSearchData: UnitStatusSearchForm | undefined =
                location.state?.unitStatusSearchResults;

            if (savedSearchData) {
                setSearchData({ search: savedSearchData });
            }
        },
        [location.state?.unitStatusSearchResults, setSearchData]
    );

    const closeSearchesAndClearLocationState = () => {
        closeSearches();
        clearWindowHistoryState();
    };

    return (
        <FrPage>
            <PageHeader
                text="Unit Status Search"
                resultsOrSavedShown={resultsOrSavedShown}
                openSavedSearch={openSavedSearch}
                showSavedSearches={showSavedSearches}
                closeSearches={closeSearchesAndClearLocationState}
            />
            <IntegratedFormProvider
                form={form}
                query={query}
                mutation={mutation}
                fuzzyMatchingEnabled={fuzzyMatchingEnabled}
            >
                <FormWrapper
                    query={query}
                    form={form}
                    resultsOrSavedShown={resultsOrSavedShown}
                    formBody={<UnitStatusSearchFormBody form={form} options={query.data} />}
                    footer={<UnitStatusSearchFormFooter form={form} />}
                />
            </IntegratedFormProvider>
            {showSavedSearches && (
                <UnitStatusSearchSavedSearches onSavedSearchClick={runSavedSearch} />
            )}
            {showSearchResults && searchData && (
                <UnitStatusSearchResults
                    savedSearchId={savedSearchId}
                    searchData={searchData}
                    closeSearch={closeSearches}
                    searchTab={uniqueSearch}
                    setSavedSearchId={setSavedSearchId}
                />
            )}
        </FrPage>
    );
}
